// frontend/src/utils/companyBranding.js
// Single source of truth for "who is this quotation/PDF from" — branch details
// win when set (a company may quote from different offices with different
// GST numbers), falling back to the company's own profile, and finally to
// generic placeholder text. Never hardcode a specific tenant's name/logo here
// — that's exactly the bug this replaces (TacklePrime's details leaking into
// every other tenant's quotations).

// company.logo_url is stored as a backend-relative path (e.g. "/uploads/logos/x.jpg")
// but the frontend runs on a different origin/port, so it must be resolved
// against the API's origin, not treated as relative to the current page.
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5150/api').replace(/\/api\/?$/, '');

export function resolveLogoUrl(logoUrl) {
  if (!logoUrl) return null;
  if (/^https?:\/\//i.test(logoUrl)) return logoUrl;
  return `${API_ORIGIN}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`;
}

// Builds the "where to pay" text from the company's own Settings → Company
// Profile → Bank Details fields. Returns null if none are set, so callers
// can fall back to a generic message instead of ever showing another
// tenant's real bank account (which is what used to happen — see
// PROJECT_STATUS.md — before this existed).
export function getBankDetailsText(company) {
  if (!company) return null;
  const lines = [];
  if (company.bank_account_name)   lines.push(`Account Name: ${company.bank_account_name}`);
  if (company.bank_name)           lines.push(`Bank Name: ${company.bank_name}`);
  if (company.bank_account_number) lines.push(`Account Number: ${company.bank_account_number}`);
  if (company.bank_ifsc)           lines.push(`IFSC Code: ${company.bank_ifsc}`);
  if (company.bank_branch)         lines.push(`Branch: ${company.bank_branch}`);
  if (company.bank_upi_id)         lines.push(`UPI ID: ${company.bank_upi_id}`);
  return lines.length ? lines.join('\n') : null;
}

export function getBrandingDetails(company, branch) {
  const address = branch?.address
    ? [branch.address, branch.city, branch.state, branch.pincode].filter(Boolean).join(', ')
    : (company?.address
        ? [company.address, company.city, company.state].filter(Boolean).join(', ')
        : '');

  return {
    name:    branch?.name       || company?.name       || 'Your Company',
    address,
    // Contact address printed on quotations, purchase orders and invoices.
    //
    // Precedence, highest first:
    //   1. company.document_contact_email — an explicit company-wide override
    //   2. branch.email — the office the creating user belongs to
    //   3. company.email — the account address, as a last resort
    //
    // The override exists because 2 alone meant the address a customer saw
    // depended on WHO raised the document: TacklePrime's Hyderabad branch put
    // one salesperson's address on every quotation created there, while
    // Nalgonda put a different one. Leaving the override empty keeps the old
    // per-branch behaviour exactly, so nothing changes for a tenant that does
    // not set it.
    email:   company?.document_contact_email || branch?.email || company?.email || '',
    // Same precedence as the email above, and for the same reason: the
    // number a customer sees should not depend on which office raised the
    // document. Blank keeps the per-branch behaviour.
    phone:   company?.document_contact_phone || branch?.phone || company?.phone || '',
    gst:     branch?.gst_number || company?.gst_number || '',
    logoUrl: resolveLogoUrl(company?.logo_url),
    website: company?.website   || '',
  };
}
